import { create } from 'zustand';
import type { ProductScanResult, SyncInfo, UserProfile } from '../types/auth';

interface SyncState {
  selectAll: boolean;
  originalSelectAll: boolean;
  productList: string[];
  originalProductList: string[];
  collectionList: string[];
  originalCollectionList: string[];
  disableProductList: string[];
  disableCollectionList: string[];
}

interface ProductScanStore {
  productScanResult: ProductScanResult | null;
  syncState: SyncState | null;
  setProductScanResult: (result: ProductScanResult | null) => void;
  setSyncState: (state: SyncState | null) => void;
  toggleProductInSyncState: (productId: string) => void;
  toggleCollectionInSyncState: (collectionId: string) => void;
  updateFromUser: (user: UserProfile) => void;
  reset: () => void;
}

export const useProductScanStore = create<ProductScanStore>()((set) => ({
  productScanResult: null,
  syncState: null,
  
  setProductScanResult: (result) => set({ productScanResult: result }),
  
  setSyncState: (state) => set({ syncState: state }),
  
  toggleProductInSyncState: (productId) => set((state) => {
    if (!state.syncState) return state;
    
    const { selectAll, productList, disableProductList } = state.syncState;
    
    if (selectAll) {
      // In selectAll mode, toggle in disableProductList
      const newDisableList = [...disableProductList];
      const index = newDisableList.indexOf(productId);
      if (index > -1) {
        newDisableList.splice(index, 1);
      } else {
        newDisableList.push(productId);
      }
      return {
        ...state,
        syncState: {
          ...state.syncState,
          disableProductList: newDisableList,
        },
      };
    } else {
      // In normal mode, toggle in productList
      const newProductList = [...productList];
      const index = newProductList.indexOf(productId);
      if (index > -1) {
        newProductList.splice(index, 1);
      } else {
        newProductList.push(productId);
      }
      return {
        ...state,
        syncState: {
          ...state.syncState,
          productList: newProductList,
        },
      };
    }
  }),
  
  toggleCollectionInSyncState: (collectionId) => set((state) => {
    if (!state.syncState) return state;
    
    const { selectAll, collectionList, disableCollectionList } = state.syncState;
    
    console.log('🔄 toggleCollectionInSyncState called:', {
      collectionId,
      selectAll,
      currentDisableList: disableCollectionList,
      currentCollectionList: collectionList
    });
    
    if (selectAll) {
      // In selectAll mode, toggle in disableCollectionList
      const newDisableList = [...disableCollectionList];
      const index = newDisableList.indexOf(collectionId);
      if (index > -1) {
        newDisableList.splice(index, 1);
        console.log('✅ Removing from disable list:', collectionId);
      } else {
        newDisableList.push(collectionId);
        console.log('❌ Adding to disable list:', collectionId);
      }
      
      const newState = {
        ...state,
        syncState: {
          ...state.syncState,
          disableCollectionList: newDisableList,
        },
      };
      
      console.log('📝 New syncState after toggle:', newState.syncState);
      return newState;
    } else {
      // In normal mode, toggle in collectionList
      const newCollectionList = [...collectionList];
      const index = newCollectionList.indexOf(collectionId);
      if (index > -1) {
        newCollectionList.splice(index, 1);
      } else {
        newCollectionList.push(collectionId);
      }
      return {
        ...state,
        syncState: {
          ...state.syncState,
          collectionList: newCollectionList,
        },
      };
    }
  }),
  
  updateFromUser: (user) => {
    if (user?.productScanResult) {
      set({ productScanResult: user.productScanResult });
    }
    
    // Update sync state from user.sync.lastPayload
    if (user?.sync?.lastPayload) {
      const { selectAll, productList, collectionList, disableProductList, disableCollectionList } = user.sync.lastPayload;
      set({
        syncState: {
          selectAll: selectAll || false,
          originalSelectAll: selectAll || false,
          productList: productList || [],
          originalProductList: productList || [],
          originalCollectionList: collectionList || [],
          collectionList: collectionList || [],
          disableProductList: disableProductList || [],
          disableCollectionList: disableCollectionList || [],
        }
      });
    }
  },
  
  reset: () => set({ productScanResult: null, syncState: null }),
}));